import { FC, ReactNode, useState } from 'react';
import { Tooltip, Upload } from 'antd';
import { RcFile, UploadChangeParam, UploadFile, UploadListType } from 'antd/lib/upload/interface';
import axios from 'axios';
import imageCompression from 'browser-image-compression';
import { CopyOutlined, UploadOutlined } from '@ant-design/icons';

import { api } from '@chess-class/sandbox';

type FileUploadType = {
  accept?: string;
  compressImage?: boolean;
  defaultFileList?: UploadFile[] | undefined;
  folderName: string;
  icon?: ReactNode;
  label?: string;
  listType?: UploadListType;
  maxCount: number;
  onChange: (
    info: UploadChangeParam,
    linkInfo: { downloadUrl?: string; uploadUrl?: string },
  ) => void;
};

export const FileUpload: FC<FileUploadType> = ({
  accept,
  compressImage = true,
  defaultFileList,
  folderName,
  icon = <UploadOutlined className="text-ant-gray-7" />,
  label = 'Загрузить',
  listType = 'picture',
  maxCount = 1,
  onChange,
}) => {
  // const uploadLink = useMutateFileUploadLink();
  const uploadLink = api.file.useFileUploadLink();
  const [fileList, setFileList] = useState(defaultFileList);
  const [isCompressing, setIsCompressing] = useState(false);
  const isEmpty = !fileList?.length;

  return (
    <Upload.Dragger
      accept={accept}
      action={(file) =>
        uploadLink
          .mutateAsync({ fileName: file.name, folderName: folderName })
          .then((upload) => upload.data.uploadUrl)
      }
      className={isEmpty ? '' : 'border-none upload-border-none'}
      customRequest={async (rcRequest) => {
        const file =
          compressImage &&
          rcRequest.file instanceof Blob &&
          ['image/gif', 'image/jpeg', 'image/png'].includes(rcRequest.file?.type) &&
          compressImage
            ? await imageCompression(rcRequest.file as RcFile, {
                alwaysKeepResolution: true,
                maxSizeMB: 0.2,
                onProgress: (progress) => setIsCompressing(progress < 100),
                useWebWorker: true,
              })
            : await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.readAsArrayBuffer(rcRequest.file as RcFile);
                reader.onload = () => {
                  resolve(reader.result);
                };
                reader.onerror = (error) => reject(error);
              });
        axios
          .put(rcRequest.action, file, {
            headers: {
              ...rcRequest.headers,
              'x-amz-acl': 'public-read',
            },
            onUploadProgress: (re) =>
              rcRequest.onProgress?.({ ...re, percent: (re.loaded * 100) / (re.total || 10000) }),
          })
          .then(rcRequest.onSuccess)
          .catch(rcRequest.onError);
      }}
      fileList={fileList}
      iconRender={
        isCompressing
          ? () => (
              <Tooltip title="Изображение сжимается">
                <CopyOutlined className="compressing" />
              </Tooltip>
            )
          : undefined
      }
      listType={listType}
      maxCount={maxCount}
      method="PUT"
      multiple={false}
      onChange={(info) => {
        setFileList(info.fileList);

        onChange(info, {
          downloadUrl: uploadLink.data?.data.downloadUrl,
          uploadUrl: uploadLink.data?.data.uploadUrl,
        });
      }}
      progress={isCompressing ? { style: { display: 'none' } } : undefined}
      showUploadList={{
        downloadIcon: undefined,
        showDownloadIcon: false,
        showPreviewIcon: false,
      }}
    >
      {isEmpty && (
        <div className="p-2">
          <p className="ant-upload-drag-icon">{icon}</p>
          {label}
        </div>
      )}
    </Upload.Dragger>
  );
};
